import type { Meta } from '@cubejs-client/core'
import { Trash2 } from 'lucide-react'
import { useState } from 'react'

import { MemberPicker, ViewPicker } from '@/components/member-picker/MemberPicker'
import { listMembers, listViews } from '@/components/member-picker/members'
import { Button } from '@/components/ui/button'
import { messages } from '@/lib/messages'
import type { Visibility } from '@/services/bi/reports'
import type { DocumentFilter, DocumentQuery } from '@/services/cube/document'

import {
  CheckboxField,
  NumberField,
  OrderEditor,
  SelectField,
  TextField,
  WhereEditor,
} from './fields'
import { queryNameError } from './query-name'
import type { DocumentTemplate } from './serialize'

const t = messages.documents.editor.template

const filterLabel = (filter: DocumentFilter) => `${t.filterKinds[filter.type]}: ${filter.member}`

function FilterDraft({
  kind,
  meta,
  onAdd,
  onCancel,
}: {
  kind: 'period' | 'multiSelect'
  meta: Meta
  onAdd: (filter: DocumentFilter) => void
  onCancel: () => void
}) {
  const [view, setView] = useState<string>()
  const [member, setMember] = useState<string>()
  const [defaultPeriod, setDefaultPeriod] = useState('-1m')
  return (
    <div className="border-input flex flex-col gap-2 rounded-sm border p-2">
      <p className="text-sm font-medium">{t.filterKinds[kind]}</p>
      <ViewPicker
        meta={meta}
        value={view}
        onChange={(next) => (setView(next), setMember(undefined))}
      />
      {view && (
        <MemberPicker
          meta={meta}
          view={view}
          kind={kind === 'period' ? 'time' : 'dimension'}
          label={t.filterMember}
          value={member}
          onChange={setMember}
        />
      )}
      {kind === 'period' && (
        <SelectField
          label={t.periodDefault}
          value={defaultPeriod}
          onChange={setDefaultPeriod}
          options={Object.entries(t.periodDefaults).map(([value, label]) => ({ value, label }))}
        />
      )}
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={!view || !member}
          onClick={() => {
            if (!view || !member) return
            onAdd(
              (kind === 'period'
                ? { view, member, type: 'period', granularity: 'month', default: defaultPeriod }
                : { view, member, type: 'multiSelect' }) as DocumentFilter,
            )
          }}
        >
          {messages.documents.editor.fields.add}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {messages.common.cancel}
        </Button>
      </div>
    </div>
  )
}

function FiltersSection({
  meta,
  filters,
  onChange,
}: {
  meta: Meta | undefined
  filters: DocumentFilter[]
  onChange: (filters: DocumentFilter[]) => void
}) {
  const [draft, setDraft] = useState<'period' | 'multiSelect'>()
  const hasPeriod = filters.some((filter) => filter.type === 'period')
  return (
    <section className="flex flex-col gap-2" aria-label={t.filters}>
      <h3 className="text-sm font-medium">{t.filters}</h3>
      {filters.length === 0 && <p className="text-muted-foreground text-sm">{t.noFilters}</p>}
      {filters.map((filter) => (
        <div
          key={`${filter.type}:${filter.view}.${filter.member}`}
          className="border-input flex items-center gap-1 rounded-sm border px-2 py-1 text-sm"
        >
          <span className="flex-1 truncate">
            {filterLabel(filter)}
            <span className="text-muted-foreground"> · {filter.view}</span>
          </span>
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={t.removeFilter(filterLabel(filter))}
            onClick={() => onChange(filters.filter((other) => other !== filter))}
          >
            <Trash2 />
          </Button>
        </div>
      ))}
      {draft && meta ? (
        <FilterDraft
          kind={draft}
          meta={meta}
          onCancel={() => setDraft(undefined)}
          onAdd={(filter) => {
            onChange([...filters, filter])
            setDraft(undefined)
          }}
        />
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={!meta || hasPeriod}
            title={hasPeriod ? t.periodOnlyOne : undefined}
            onClick={() => setDraft('period')}
          >
            {t.addPeriod}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!meta}
            onClick={() => setDraft('multiSelect')}
          >
            {t.addMultiSelect}
          </Button>
        </div>
      )}
    </section>
  )
}

function QueryEditor({
  meta,
  name,
  query,
  usage,
  onChange,
  onDelete,
}: {
  meta: Meta
  name: string
  query: DocumentQuery
  usage: number
  onChange: (query: DocumentQuery) => void
  onDelete: () => void
}) {
  const dimensionOptions = listMembers(meta, query.view, 'dimension').map((option) => option.value)
  return (
    <details className="border-input rounded-sm border">
      <summary className="cursor-pointer px-2 py-1 text-sm font-medium">
        {name}
        <span className="text-muted-foreground font-normal">
          {' · '}
          {query.view}
          {usage > 0 && ` · ${t.usedBy(usage)}`}
        </span>
      </summary>
      <div className="flex flex-col gap-3 p-2">
        <ViewPicker
          meta={meta}
          value={query.view}
          onChange={(view) => {
            // Üyeler eski view'a aittir: yeni view'ın ilk ölçüsüyle başlanır.
            const measure = listMembers(meta, view, 'measure')[0]?.value
            onChange({
              ...query,
              view,
              measures: measure ? [measure] : query.measures,
              dimensions: [],
              order: [],
              where: [],
            })
          }}
        />
        <MemberPicker
          multiple
          meta={meta}
          view={query.view}
          kind="measure"
          value={query.measures}
          onChange={(measures) => onChange({ ...query, measures })}
        />
        <MemberPicker
          multiple
          meta={meta}
          view={query.view}
          kind="dimension"
          value={query.dimensions}
          onChange={(dimensions) => onChange({ ...query, dimensions })}
        />
        <OrderEditor
          label={messages.documents.editor.block.order}
          value={query.order}
          members={[...query.measures, ...query.dimensions]}
          onChange={(order) => onChange({ ...query, order })}
        />
        <WhereEditor
          label={messages.documents.editor.block.where}
          value={query.where}
          members={dimensionOptions}
          onChange={(where) => onChange({ ...query, where })}
        />
        <CheckboxField
          label={t.compare}
          checked={query.compare === 'previous'}
          onChange={(checked) => {
            const { compare: _compare, ...rest } = query
            onChange(checked ? { ...rest, compare: 'previous' } : rest)
          }}
        />
        <NumberField
          label={messages.documents.editor.block.limit}
          value={query.limit}
          onChange={(limit) => {
            const { limit: _limit, ...rest } = query
            onChange(limit === undefined ? rest : { ...rest, limit })
          }}
        />
        <div>
          <Button
            variant="outline"
            size="sm"
            disabled={usage > 0}
            title={usage > 0 ? t.deleteBlocked : undefined}
            onClick={onDelete}
          >
            <Trash2 />
            {t.deleteQuery(name)}
          </Button>
        </div>
      </div>
    </details>
  )
}

function QueriesSection({
  meta,
  template,
  usage,
  onChange,
}: {
  meta: Meta | undefined
  template: DocumentTemplate
  usage: (name: string) => number
  onChange: (queries: DocumentTemplate['queries']) => void
}) {
  const [name, setName] = useState('')
  const names = Object.keys(template.queries)
  const error = name === '' ? undefined : queryNameError(name, names)

  const create = () => {
    if (!meta || name === '' || error) return
    // Başlangıç view'ı: şablon filtrelerinin view'ı (aynı veri), yoksa ilk ölçülü view.
    const views = listViews(meta).map((option) => option.value)
    const candidates = [...template.filters.map((filter) => filter.view), ...views]
    const view = candidates.find(
      (candidate) =>
        views.includes(candidate) && listMembers(meta, candidate, 'measure').length > 0,
    )
    const measure = view ? listMembers(meta, view, 'measure')[0]?.value : undefined
    if (!view || !measure) return
    onChange({
      ...template.queries,
      [name]: { view, measures: [measure], dimensions: [], order: [], where: [] },
    })
    setName('')
  }

  return (
    <section className="flex flex-col gap-2" aria-label={t.queries}>
      <h3 className="text-sm font-medium">{t.queries}</h3>
      {names.length === 0 && <p className="text-muted-foreground text-sm">{t.noQueries}</p>}
      {meta &&
        names.map((queryName) => (
          <QueryEditor
            key={queryName}
            meta={meta}
            name={queryName}
            query={template.queries[queryName] as DocumentQuery}
            usage={usage(queryName)}
            onChange={(query) => onChange({ ...template.queries, [queryName]: query })}
            onDelete={() => {
              const { [queryName]: _removed, ...rest } = template.queries
              onChange(rest)
            }}
          />
        ))}
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <TextField label={t.queryName} value={name} error={error} onChange={setName} />
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!meta || name === '' || !!error}
          onClick={create}
        >
          {t.addQuery}
        </Button>
      </div>
    </section>
  )
}

export function TemplateSettings({
  meta,
  template,
  onChange,
  usage,
  visibility,
  onVisibilityChange,
}: {
  meta: Meta | undefined
  template: DocumentTemplate
  onChange: (template: DocumentTemplate) => void
  /** Sorguya yapılan başvuru sayısı (silmeyi engellemek için). */
  usage: (queryName: string) => number
  visibility: Visibility
  onVisibilityChange: (visibility: Visibility) => void
}) {
  return (
    <div className="flex flex-col gap-4">
      <TextField
        label={t.title}
        value={template.title}
        onChange={(title) => onChange({ ...template, title })}
      />
      <SelectField
        label={t.orientation}
        value={template.orientation}
        onChange={(orientation) =>
          onChange({ ...template, orientation: orientation as DocumentTemplate['orientation'] })
        }
        options={[
          { value: 'portrait', label: t.portrait },
          { value: 'landscape', label: t.landscape },
        ]}
      />
      <SelectField
        label={t.visibility}
        value={visibility}
        onChange={(next) => onVisibilityChange(next as Visibility)}
        // Grup paylaşımı grup seçici ister; burada yalnız mevcut grup paylaşımı korunur.
        options={[
          { value: 'personal', label: t.visibilityKinds.personal },
          ...(visibility === 'groups'
            ? [{ value: 'groups', label: t.visibilityKinds.groups }]
            : []),
          { value: 'org', label: t.visibilityKinds.org },
        ]}
      />
      <FiltersSection
        meta={meta}
        filters={template.filters}
        onChange={(filters) => onChange({ ...template, filters })}
      />
      <QueriesSection
        meta={meta}
        template={template}
        usage={usage}
        onChange={(queries) => onChange({ ...template, queries })}
      />
    </div>
  )
}
